import { logger } from '@teko/log';
import { useTenantConfig } from 'contexts';
import { useState } from 'react';
import { getProductReviews } from 'services';
import useSWR from 'swr';
import {
  ConvertedProduct,
  IGetProductReviewResponse,
  IGetProductReviewsParams,
  IProductReview,
  IRatingData,
} from 'types';

const DEFAULT_GET_REVIEWS_PAGE_SIZE = 5;

export const useProductRatingList = (
  product?: ConvertedProduct,
): IRatingData => {
  const { platformId } = useTenantConfig();
  const [filterParams, setFilterParams] =
    useState<
      Pick<IGetProductReviewsParams, 'rating' | 'hasImage' | 'hasVideo'>
    >();
  const [page, setPage] = useState(1);
  const [reviews, setReviews] = useState<IProductReview[]>([]);

  const getKey = () => {
    // have no productId -> stop fetching
    if (!product?.productGroup?.id) return null;

    return [
      `/product-reviews/${product?.productGroup?.id}`,
      page,
      filterParams?.rating,
      filterParams?.hasImage,
      filterParams?.hasVideo,
    ];
  };

  const {
    data,
    isValidating,
    mutate: refreshReviewList,
  } = useSWR<IGetProductReviewResponse | undefined>(
    getKey,
    async () => {
      try {
        if (!product?.productGroup?.id) return undefined;
        const res = await getProductReviews(product.productGroup.id, {
          ...filterParams,
          page,
          pageSize: DEFAULT_GET_REVIEWS_PAGE_SIZE,
          platformId,
        });
        return res;
      } catch (error) {
        logger.error(`Error occur when getProductReviews: ${error}`);
        return undefined;
      }
    },
    {
      onSuccess: (data) => {
        const newReviews =
          data?.pagination?.page === 1
            ? data.reviews
            : [...reviews, ...(data?.reviews || [])];
        setReviews(newReviews);
      },
      revalidateOnFocus: false,
    },
  );

  const { ratingCounts, averageRating, totalReviews } = data || {};

  return {
    data,
    reviews,
    ratingCounts,
    averageRating,
    totalReviews,
    filterParams,
    setFilterParams,
    loadMore: () => setPage((prev) => prev + 1),
    hasMore: reviews.length < (data?.pagination?.total || 0),
    noFilter: !filterParams || Object.values(filterParams).every((val) => !val),
    numberOfReviewsRemaining: (data?.pagination?.total || 0) - reviews.length,
    setPage,
    isValidating,
    refreshReviewList,
  };
};
